var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spread = (this && this.__spread) || function () {
    for (var ar = [], i = 0; i < arguments.length; i++) ar = ar.concat(__read(arguments[i]));
    return ar;
};
import { State, Action, Selector, Store } from '@ngxs/store';
import { HistoryStateModel } from './history.state.model';
import { AddHistory, Undo, Redo, ResetHistory, CacheChapterSynchronization, RevertChapterSynchronization, ClearHistory, RemoveChapterSynchronization, CopyChapterSynchronization } from './history.actions';
import { HistoryAction } from '../models/history-action.enum';
import { ToggleVisibleNodes } from 'src/app/engine/state/nodes/engine.nodes.actions';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { EngineService } from 'src/app/engine/engine.service';
import { SetChapter } from 'src/app/story/state/chapters/story.chapters.actions';
import { ChapterService } from 'src/app/story/chapter.service';
var initialState = {
    histories: [],
    current: -1,
    cache: {}
};
var HistoryState = /** @class */ (function () {
    function HistoryState(store, engineService, chapterService) {
        this.store = store;
        this.engineService = engineService;
        this.chapterService = chapterService;
    }
    /**
     * Selectors
     */
    HistoryState.getHistories = function (state) {
        return state.histories;
    };
    HistoryState.getCurrent = function (state) {
        return state.current;
    };
    /**
     * Actions
     */
    HistoryState.prototype.addHistory = function (context, action) {
        // tslint:disable-next-line:prefer-const
        var _a = context.getState(), current = _a.current, histories = _a.histories;
        // When recording history, eliminate all future events
        histories = histories.slice(0, current + 1);
        // Limit history to 100 entries to prevent memory overflow
        if (histories.length >= 100) {
            histories.unshift();
        }
        histories.push(action.history);
        // Update current index
        current = histories.length - 1;
        context.patchState({
            histories: __spread(histories),
            current: current
        });
    };
    HistoryState.prototype.redo = function (context, action) {
        var histories = __spread(context.getState().histories);
        var current = context.getState().current + 1;
        var history = histories[current];
        if (!history) {
            return;
        }
        switch (history.action) {
            case HistoryAction.SetNodeVisible:
                this.toggleVisibleNodes(context, history);
                break;
        }
        context.patchState({
            current: current
        });
    };
    HistoryState.prototype.undo = function (context, action) {
        var histories = __spread(context.getState().histories);
        var current = context.getState().current - 1;
        // No undos
        if (!histories.length || current < 0) {
            return;
        }
        var history = histories[current];
        switch (history.action) {
            case HistoryAction.SetNodeVisible:
                this.toggleVisibleNodes(context, history);
                break;
        }
        context.patchState({
            current: current
        });
    };
    HistoryState.prototype.resetHistory = function (context, action) {
        context.setState(initialState);
    };
    HistoryState.prototype.ClearHistory = function (context, action) {
        context.patchState({
            histories: [],
            current: -1,
        });
    };
    HistoryState.prototype.toggleVisibleNodes = function (context, history) {
        var nodes = Object.values(history.value);
        var visibleNodes = nodes
            .filter(function (node) { return node.boolState.shown; });
        var hiddenNodes = nodes
            .filter(function (node) { return !node.boolState.shown; });
        context.dispatch(new ToggleVisibleNodes(visibleNodes, true, false));
        context.dispatch(new ToggleVisibleNodes(hiddenNodes, false, false));
    };
    HistoryState.prototype.cacheChapterSynchronization = function (context, action) {
        var chapterId = action.chapterId, cacheId = action.cacheId;
        var cacheKey = this.getCacheKey(cacheId, chapterId);
        var cache = context.getState().cache;
        // When readOnly is flagged, don't overwrite pre-existing values
        if (action.readOnly) {
            var synchronization = cache[cacheKey];
            if (synchronization) {
                return;
            }
        }
        cache[cacheKey] = __assign({}, action.synchronization);
        context.patchState({
            cache: __assign({}, cache)
        });
    };
    HistoryState.prototype.revertChapterSynchronization = function (context, action) {
        // Access the cache
        var chapterId = action.chapterId, cacheId = action.cacheId;
        var synchronization = this.getSynchronization(context, cacheId, chapterId);
        if (synchronization) {
            // Revert the chapter synchronization
            var chapter_1 = this.store.selectSnapshot(StoryChaptersState.getStoryChapters)
                .find(function (_chapter) { return _chapter.id === chapterId; });
            chapter_1 = __assign({}, chapter_1);
            chapter_1.synchronization = synchronization;
            // Update the engine, which should then propagate back to the other states
            this.engineService.setGraphState(chapter_1.synchronization);
            // Update state and engine
            var index = this.store.selectSnapshot(StoryChaptersState.getStoryChapters)
                .findIndex(function (_chapter) { return chapter_1.id === _chapter.id; });
            this.store.dispatch(new SetChapter(chapter_1));
            this.chapterService.addChapterToEngine(chapter_1, index);
        }
    };
    HistoryState.prototype.removeChapterSynchronization = function (context, action) {
        var cacheKey = this.getCacheKey(action.cacheId, action.chapterId);
        var cache = __assign({}, context.getState().cache);
        delete cache[cacheKey];
        context.patchState({
            cache: __assign({}, cache)
        });
    };
    HistoryState.prototype.copyChapterSynchronization = function (context, action) {
        var synchronization = this.getSynchronization(context, action.sourceCacheId, action.sourceChapterId);
        context.dispatch(new CacheChapterSynchronization(action.targetCacheId, action.targetChapterId, synchronization));
    };
    HistoryState.prototype.getSynchronization = function (context, cacheId, chapterId) {
        var cacheKey = this.getCacheKey(cacheId, chapterId);
        var cache = context.getState().cache;
        return cache[cacheKey];
    };
    HistoryState.prototype.getCacheKey = function (cacheId, chapterId) {
        return cacheId + "_" + chapterId;
    };
    __decorate([
        Action(AddHistory),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object, AddHistory]),
        __metadata("design:returntype", void 0)
    ], HistoryState.prototype, "addHistory", null);
    __decorate([
        Action(Redo),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object, Redo]),
        __metadata("design:returntype", void 0)
    ], HistoryState.prototype, "redo", null);
    __decorate([
        Action(Undo),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object, Undo]),
        __metadata("design:returntype", void 0)
    ], HistoryState.prototype, "undo", null);
    __decorate([
        Action(ResetHistory),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object, ResetHistory]),
        __metadata("design:returntype", void 0)
    ], HistoryState.prototype, "resetHistory", null);
    __decorate([
        Action(ClearHistory),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object, ClearHistory]),
        __metadata("design:returntype", void 0)
    ], HistoryState.prototype, "ClearHistory", null);
    __decorate([
        Action(CacheChapterSynchronization),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object, CacheChapterSynchronization]),
        __metadata("design:returntype", void 0)
    ], HistoryState.prototype, "cacheChapterSynchronization", null);
    __decorate([
        Action(RevertChapterSynchronization),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object, RevertChapterSynchronization]),
        __metadata("design:returntype", void 0)
    ], HistoryState.prototype, "revertChapterSynchronization", null);
    __decorate([
        Action(RemoveChapterSynchronization),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object, RemoveChapterSynchronization]),
        __metadata("design:returntype", void 0)
    ], HistoryState.prototype, "removeChapterSynchronization", null);
    __decorate([
        Action(CopyChapterSynchronization),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object, CopyChapterSynchronization]),
        __metadata("design:returntype", void 0)
    ], HistoryState.prototype, "copyChapterSynchronization", null);
    __decorate([
        Selector(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [HistoryStateModel]),
        __metadata("design:returntype", void 0)
    ], HistoryState, "getHistories", null);
    __decorate([
        Selector(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [HistoryStateModel]),
        __metadata("design:returntype", void 0)
    ], HistoryState, "getCurrent", null);
    HistoryState = __decorate([
        State({
            name: 'history',
            defaults: initialState
        }),
        __metadata("design:paramtypes", [Store,
            EngineService,
            ChapterService])
    ], HistoryState);
    return HistoryState;
}());
export { HistoryState };
